import { Injectable } from '@angular/core';
import { merge, Observable, of, throwError, timer } from 'rxjs';
import {
  catchError,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import {
  FAILED,
  MFA_FAILED,
  PUSH_CONFIRMATION_REJECTED,
  PUSH_CONFIRMATION_TIMED_OUT,
  PUSH_CONFIRMATION_WAITING
} from '../../../constants';
import {
  AuthAccessTokenDetails,
  CancelAuthentication,
  FlyDeltaPoll,
  InitiateAuthentication,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  SwapAuthForAccessTokens
} from '../../../models';
import { transformSwapAuthTokenDetails } from '../login-remember-me-utils-service/initiate-login-utils';
import { LoginSubmitAuthenticationIdentifierHelperExtendedService } from './login-submit-authentication-identifier-helper-extended.service';

@Injectable({
  providedIn: 'root'
})
export class LoginSubmitAuthenticationIdentifierHelperService extends LoginSubmitAuthenticationIdentifierHelperExtendedService {
  savedTokenCheck(): boolean {
    return !!(this.encyToken && this.uid);
  }

  checkFlowId(response: InitiateAuthentication): string {
    return response.id ? response.id : '';
  }

  handleFlyDeltaPollResponse(response: FlyDeltaPoll): Observable<string> {
    if (
      response.status &&
      [
        PUSH_CONFIRMATION_TIMED_OUT,
        MFA_FAILED,
        FAILED,
        PUSH_CONFIRMATION_REJECTED
      ].includes(response.status)
    ) {
      return throwError(`ERROR_MSG_${response.status}`);
    } else if (response.status === PUSH_CONFIRMATION_WAITING) {
      return of(response.status);
    } else {
      return throwError(response);
    }
  }

  initiateFlyDeltaPoll(
    loginOAuthParams: LoginOAuthParams,
    flowIdValue: string
  ): Observable<FlyDeltaPoll> {
    const pollTime =
      loginOAuthParams.oauthConfig.FLY_DELTA_POLLING_TIMER ?? 5000;

    const init$ = this.initiateAuthentication
      .initiateAuthentication(loginOAuthParams.oauthConfig, flowIdValue)
      .pipe(shareReplay());

    const poll$ = timer(0, pollTime).pipe(
      withLatestFrom(init$),
      switchMap(([, res]) => {
        if (res.status && [MFA_FAILED, FAILED].includes(res.status)) {
          this.flowId = '';
          return throwError(`ERROR_MSG_${res.status}`);
        } else {
          this.flowId = this.checkFlowId(res);
          return this.flyDeltaPoll.submitFlyDeltaPoll(
            loginOAuthParams.oauthConfig,
            this.checkFlowId(res)
          );
        }
      }),
      takeUntil(this.stopPoll$),
      takeWhile((val) => val.status === PUSH_CONFIRMATION_WAITING, true)
    );

    return merge(init$, poll$).pipe(
      tap((res: FlyDeltaPoll) => {
        return of(res);
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  cancelPasswordlessLogin(
    loginOAuthParams: LoginOAuthParams
  ): Observable<string> {
    return this.cancelAuthentication
      .cancelAuthentication(
        loginOAuthParams.oauthConfig,
        this.flowId ? this.flowId : ''
      )
      .pipe(
        map((res: CancelAuthentication) => {
          this.stopPoll$.next();
          this.flowId = '';
          return res.status ? `ERROR_MSG_${res.status}` : '';
        }),
        catchError((error) => {
          return of(
            this.loginRememberMeErrorUtil.checkRememberMeErrors(
              loginOAuthParams.oauthConfig,
              error
            )
          );
        })
      );
  }

  createAuthTokenDetails(
    response: SwapAuthForAccessTokens
  ): AuthAccessTokenDetails {
    const authAccessTokenDetails = transformSwapAuthTokenDetails(response);
    return this.savedTokenCheck()
      ? {
          ...authAccessTokenDetails,
          encyToken: this.encyToken,
          uid: this.uid
        }
      : authAccessTokenDetails;
  }

  assignRememberMeDetails(response: LoginAuthWithCredentialsResponse) {
    this.encyToken = response.encryptedToken ? response.encryptedToken : '';
    this.uid = response.fingerprint ? response.fingerprint : '';
  }
}
